import type { UseQueryOptions } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';

import { policyService } from '../services/policy.service';

import type { ApiResponse, ErrorResponse } from '../services/api.types';
import type { PolicyPeriodViewModel } from '../types/PolicyPeriodViewModel';
import type { PolicyTimelineViewModel } from '../types/PolicyTimelineViewModel';
import type { UnitStatReportViewModel } from '../types/UnitStatReportViewModel';
import type { PolicyExposureViewModel } from '../types/PolicyExposureViewModel';
import type { UnitStatSummaryViewModel } from '../types/UnitStatSummaryViewModel';
import type { PolicyComplianceViewModel } from '../types/PolicyComplianceViewModel';
import type { PolicyEndorsementViewModel } from '../types/PolicyEndorsementViewModel';
import type { PolicyPremiumDetailsViewModel } from '../types/PolicyPremiumDetailsViewModel';
import type { UnitStatReportSummaryViewModel } from '../types/UnitStatReportSummaryViewModel';
import type { PolicyNamesAndAddressesViewModel } from '../types/PolicyNamesAndAddressesViewModel';
import type { PolicyTransactionHistoryViewModel } from '../types/PolicyTransactionHistoryViewModel';
import type { UnitStatReportLossSummaryViewModel } from '../types/UnitStatReportLossSummaryViewModel';
import type { StateLookupOption, UnitStatUsrLookups } from '../services/policy.service';

export const useEndorsementsByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyEndorsementViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyEndorsementViewModel[]>, ErrorResponse>({
    queryKey: ['policyEndorsementsData', comboId, coverageId, policyId],
    queryFn: () => policyService.getEndorsementsByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const usePremiumsByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyPremiumDetailsViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyPremiumDetailsViewModel[]>, ErrorResponse>({
    queryKey: ['policyPremiumsData', comboId, coverageId, policyId],
    queryFn: () => policyService.getPremiumsByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useComplianceHistoryByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyComplianceViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyComplianceViewModel[]>, ErrorResponse>({
    queryKey: ['policyComplianceHistoryData', comboId, coverageId, policyId],
    queryFn: () => policyService.getComplianceHistoryByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useUnitStatReportVersionsByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<UnitStatReportSummaryViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<UnitStatReportSummaryViewModel[]>, ErrorResponse>({
    queryKey: ['policyUnitStatReportVersionsData', comboId, coverageId, policyId],
    queryFn: () => policyService.getUnitStatReportVersionsByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useUnitStatReportByPolicyIdAndReportNumber = (
  comboId: number,
  coverageId: number,
  policyId: number | undefined,
  reportNumber: number | undefined,
  correctionSequenceNumber: number | undefined,
  options?: UseQueryOptions<ApiResponse<UnitStatReportViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<UnitStatReportViewModel>, ErrorResponse>({
    queryKey: [
      'policyUnitStatReport',
      comboId,
      coverageId,
      policyId,
      reportNumber,
      correctionSequenceNumber,
    ],
    queryFn: () =>
      policyService.getUnitStatReportByPolicyIdAndReportNumber(
        comboId,
        coverageId,
        policyId!,
        reportNumber!,
        correctionSequenceNumber!
      ),
    enabled:
      !!comboId &&
      !!coverageId &&
      !!policyId &&
      reportNumber !== undefined &&
      correctionSequenceNumber !== undefined,
    ...options,
  });

export const useUnitStatUsrLookups = (
  options?: UseQueryOptions<ApiResponse<UnitStatUsrLookups>, ErrorResponse>
) =>
  useQuery<ApiResponse<UnitStatUsrLookups>, ErrorResponse>({
    queryKey: ['unitStatUsrLookups'],
    queryFn: () => policyService.getUnitStatUsrLookups(),
    ...options,
  });

export const useStateLookups = (
  options?: UseQueryOptions<ApiResponse<StateLookupOption[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<StateLookupOption[]>, ErrorResponse>({
    queryKey: ['stateLookups'],
    queryFn: () => policyService.getStateLookups(),
    ...options,
  });

export const usePolicyPeriodsByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyPeriodViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyPeriodViewModel[]>, ErrorResponse>({
    queryKey: ['policyPeriodsData', comboId, coverageId, policyId],
    queryFn: () => policyService.getPeriodsByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const usePolicyExposuresByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyExposureViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyExposureViewModel[]>, ErrorResponse>({
    queryKey: ['policyExposuresData', comboId, coverageId, policyId],
    queryFn: () => policyService.getExposuresByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const usePolicyTimelineByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyTimelineViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyTimelineViewModel>, ErrorResponse>({
    queryKey: ['policyTimelineData', comboId, coverageId, policyId],
    queryFn: () => policyService.getPolicyTimelineByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const usePolicyNamesAndAddresses = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyNamesAndAddressesViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyNamesAndAddressesViewModel>, ErrorResponse>({
    queryKey: ['policyNamesAndAddressesData', comboId, coverageId, policyId],
    queryFn: () => policyService.getPolicyNamesAndAddresses(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useUnitStatLossesSummaryByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<UnitStatReportLossSummaryViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<UnitStatReportLossSummaryViewModel[]>, ErrorResponse>({
    queryKey: ['policyUnitStatLossesSummaryData', comboId, coverageId, policyId],
    queryFn: () => policyService.getUnitStatLossesSummaryByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useUnitStatSummaryByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<UnitStatSummaryViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<UnitStatSummaryViewModel[]>, ErrorResponse>({
    queryKey: ['policyUnitStatSummaryData', comboId, coverageId, policyId],
    queryFn: () => policyService.getUnitStatSummaryByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useTransactionHistoryByPolicyId = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyTransactionHistoryViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyTransactionHistoryViewModel[]>, ErrorResponse>({
    queryKey: ['policyTransactionHistoryData', comboId, coverageId, policyId],
    queryFn: () => policyService.getTransactionHistoryByPolicyId(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });
